import { useEffect } from "react";
import type { BrandDefinition } from "@school-sales-ai/shared";
import { Wordmark } from "@/components/Wordmark";
import { brandMarkSrc } from "@/lib/brand";

/**
 * The legal entity is the same for every brand: one company is the Responsable
 * for both products, so a per-brand name here would be a different claim about
 * who holds the data. Only the product NAME and the canonical URL vary.
 */
export const LEGAL_ENTITY = "TECNOLOGIAS GRAB SA de CV";
export const LEGAL_CONTACT_EMAIL = "bernardo@scholaops.ai";

type LegalPageShellProps = {
  brand: BrandDefinition;
  /** Absolute canonical URL of this page on the brand's own domain. */
  canonicalHref: string;
  /** Already formatted, e.g. "28 de septiembre de 2026". */
  lastUpdated: string;
  children: React.ReactNode;
};

/**
 * Header, footer and canonical link shared by the two public legal pages (the
 * aviso de privacidad and the términos y condiciones), so they cannot drift
 * into two different-looking documents from the same company.
 */
export function LegalPageShell({
  brand,
  canonicalHref,
  lastUpdated,
  children,
}: LegalPageShellProps): React.JSX.Element {
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "canonical";
    // Built from the brand, never hardcoded: on the other brand's domain a fixed
    // app.scholaops.ai would canonicalise this page to a competitor of itself.
    link.href = canonicalHref;
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, [canonicalHref]);

  return (
    <div className="min-h-[100dvh] bg-ink-050 font-body">
      <header className="bg-white border-b border-ink-100 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <div className="inline-flex items-center gap-2">
            <img
              src={brandMarkSrc(brand)}
              alt=""
              className="h-[22px] w-[22px] flex-shrink-0"
            />
            <Wordmark brand={brand} className="text-[17px]" />
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 pb-16">{children}</main>

      <footer className="border-t border-ink-100 py-6 text-center text-xs text-ink-500 space-y-1">
        <p>
          © {new Date().getFullYear()} {LEGAL_ENTITY} · Todos los derechos
          reservados
        </p>
        <p>Última actualización: {lastUpdated}</p>
      </footer>
    </div>
  );
}

/** The typography every legal document body renders in. */
export const LEGAL_PROSE_CLASS =
  "prose prose-sm sm:prose lg:prose-lg prose-headings:font-display prose-headings:text-ink-900 prose-a:text-moss prose-a:no-underline hover:prose-a:underline max-w-none";
